import { useState, useEffect } from "react";
import axiosInstance from "@/config/axiosInstance";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/config/apiEndpoints";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Loader2, ArrowRight } from "lucide-react"; // ← Add Loader2
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

/* ================= Helpers ================= */

const isOneTimeName = (name?: string) =>
  /one[-_ ]?time/i.test(name || "");

const displayBaseName = (name?: string) =>
  (name || "")
    .replace(/\s*(Annual(?:\s+Plan)?|Monthly|One[-_ ]?Time)/gi, "")
    .trim();

// FIXED PLAN ORDER
const PLAN_ORDER = ["one-time", "starter", "pro", "pro+"];

// Helper to get sort index for plan ordering
const getPlanSortIndex = (planName: string): number => {
  const baseName = planName
    .replace(/\s*(Annual(?:\s+Plan)?|Monthly|One[-_ ]?Time)/gi, "")
    .trim()
    .toLowerCase();

  const index = PLAN_ORDER.findIndex((order) =>
    baseName.includes(order.toLowerCase())
  );
  return index >= 0 ? index : PLAN_ORDER.length;
};

const isTrialEligiblePlan = (plan: any, hasUsedFreeTrial: boolean) => {
  const isOneTimePlan = Boolean(plan?.is_one_time) || plan?.billing_cycle === "one_time" || isOneTimeName(plan?.name);
  return !isOneTimePlan && !hasUsedFreeTrial;
};

/* ================= Types ================= */

interface PricingProps {
  inModal?: boolean;
  // onPlanSelected optionally receives the selected plan and an optional checkoutUrl
  onPlanSelected?: (plan?: any, checkoutUrl?: string | null) => void;
}

/* ================= Pricing ================= */

const Pricing = ({ inModal = false, onPlanSelected }: PricingProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [userChecked, setUserChecked] = useState(false);
  const [hasPlan, setHasPlan] = useState<boolean | null>(null);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [trialEnabled, setTrialEnabled] = useState(true);
  // Site-wide trial toggle (Site Settings in admin) folded into the same
  // "already used trial" signal every downstream check already understands —
  // disabling it site-wide behaves exactly like the user having used theirs.
  const effectiveHasUsedFreeTrial = hasUsedFreeTrial || !trialEnabled;

  /* ---------- Site settings (is the free trial currently offered?) ---------- */

  useEffect(() => {
    axiosInstance
      .get(endpoints.site_settings)
      .then((res) => setTrialEnabled(res.data?.trial_enabled ?? true))
      .catch(() => setTrialEnabled(true));
  }, []);

  /* ---------- Auth check ---------- */

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("access_token");

      if (!token) {
        setHasPlan(false);
        setUserChecked(true);
        return;
      }

      try {
        const res = await axiosInstance.get(endpoints.me);
        const userHasPlan = !!res.data.plan;
        const userHasUsedTrial = res.data.has_used_free_trial || false;

        localStorage.setItem("has_plan", userHasPlan ? "true" : "false");

        setHasUsedFreeTrial(userHasUsedTrial);
        setCurrentUser(res.data);

        if (
          !inModal &&
          userHasPlan &&
          (window.location.pathname === "/" ||
            window.location.pathname === "/pricing")
        ) {
          navigate("/dashboard", { replace: true });
          return;
        }

        setHasPlan(userHasPlan);
      } catch {
        setHasPlan(false);
      } finally {
        setUserChecked(true);
      }
    };

    checkAuth();
  }, [navigate, inModal]);

  /* ---------- Fetch plans ---------- */

  useEffect(() => {
    if (userChecked) {
      const fetchPlans = async () => {
        try {
          const res = await axiosInstance.get(endpoints.plans);
          setPricingPlans(res.data);
        } catch (err) {
          console.error("Failed to load plans:", err);
          toast({
            title: "Failed to load plans",
            description: "Please refresh the page or try again later.",
            variant: "destructive",
          });
        } finally {
          setPlansLoading(false);
        }
      };

      fetchPlans();
    } else {
      setPlansLoading(false);
    }
  }, [userChecked, hasPlan, toast]);

  // if (plansLoading) {
  //   return (
  //     <div className="text-center py-20 text-muted-foreground">
  //       <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
  //       <p>Loading pricing plans...</p>
  //     </div>
  //   );
  // }

  /* ================= Render ================= */

  return (
    <section id="pricing" className="py-10 bg-background">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Simple, Transparent{" "}
            <span className="text-red-500">Pricing</span>
          </h2>
          <p className="text-muted-foreground">
            Choose the plan that fits your business. All plans include our core features with no hidden fees.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center mb-5">
          <div className="inline-flex bg-muted rounded-full p-1">
            {(["monthly", "annual"] as const).map((cycle) => (
              <button
                key={cycle}
                onClick={() => setBillingCycle(cycle)}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition ${billingCycle === cycle
                  ? "bg-background shadow"
                  : "text-muted-foreground"
                  }`}
              >
                {cycle === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>

        </div>
        <div
          className={`
                mt-2 flex justify-center transition-all duration-300 ease-in-out
                ${billingCycle === "annual" ? "opacity-100 translate-y-0 scale-100" : "opacity-70 translate-y-0 scale-95"}
              `}
        >
           <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-gradient-to-r from-green-50 via-emerald-50 to-green-100 px-4 py-1.5 shadow-sm">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-[10px] font-bold text-white">
              %
            </span>
            <span className="text-xs sm:text-sm font-medium text-green-800">
              Go Annual. Save 10% More
            </span>
          </div>
        </div>

        {/* Plans */}
        <div
          className={`mt-5 grid gap-8 grid-cols-1 md:grid-cols-2 ${billingCycle === "annual" ? "lg:grid-cols-3" : "lg:grid-cols-4"
            }`}
        >
          {/* Show Loader Cards while API loads */}
          {plansLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="h-[420px] flex items-center justify-center">
                <CardContent className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-muted-foreground text-sm">Loading plan...</p>
                </CardContent>
              </Card>
            ))}

          {!plansLoading && (
            <>
              {/* One-time plans */}
              {billingCycle === "monthly" &&
                pricingPlans
                  .filter((p) => isOneTimeName(p.name))
                  .sort(
                    (a, b) => getPlanSortIndex(a.name) - getPlanSortIndex(b.name)
                  )
                  .map((plan) => (
                          <PricingCard
                      key={plan.id}
                      plan={plan}
                      isOneTime
                      inModal={inModal}
                            currentUser={currentUser}
                      onPlanSelected={onPlanSelected}
                      billingCycle={billingCycle}
                      allPlans={pricingPlans}
                      hasUsedFreeTrial={effectiveHasUsedFreeTrial}
                      hasPlan={hasPlan}
                    />
                  ))}

              {/* Monthly / Annual */}

{Object.values(
  pricingPlans.reduce((acc: any, plan: any) => {
    const base = displayBaseName(plan.name);

    if (!acc[base]) {
      acc[base] = {
        monthly: null,
        annual: null,
        monthlyFeatures: [],
        annualFeatures: [],
      };
    }

    if (plan.billing_cycle === "monthly") {
      acc[base].monthly = plan;
      acc[base].monthlyFeatures = Array.isArray(plan.features) ? plan.features : [];
    } else if (plan.billing_cycle === "annual") {
      acc[base].annual = plan;
      acc[base].annualFeatures = Array.isArray(plan.features) ? plan.features : [];
    }

    return acc;
  }, {})
)
  .sort((a: any, b: any) => {
    const planA = a.monthly || a.annual;
    const planB = b.monthly || b.annual;
    return getPlanSortIndex(planA.name) - getPlanSortIndex(planB.name);
  })
  .map((group: any) => {
    const plan = billingCycle === "monthly" ? group.monthly : group.annual;

    if (!plan || isOneTimeName(plan.name)) return null;

    // Use correct features based on billing cycle
    const featuresToShow =
      billingCycle === "monthly"
        ? group.monthlyFeatures
        : group.annualFeatures;

    return (
      <PricingCard
        key={plan.id}
        plan={plan}
        features={featuresToShow}           // ← This is the important fix
        inModal={inModal}
        onPlanSelected={onPlanSelected}
        billingCycle={billingCycle}
        allPlans={pricingPlans}
        hasUsedFreeTrial={effectiveHasUsedFreeTrial}
        hasPlan={hasPlan}
        currentUser={currentUser}
      />
    );
  })}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

/* ================= Pricing Card ================= */

interface PricingCardProps {
  plan: any;
  features?: any[];
  isOneTime?: boolean;
  inModal?: boolean;
  onPlanSelected?: (plan: any, checkoutUrl?: string | null) => void;
  billingCycle?: "monthly" | "annual";
  allPlans?: any[];
  hasUsedFreeTrial?: boolean;
  hasPlan?: boolean | null;
  isLoadingPlans?: boolean; // ← NEW: to show loader inside card
  currentUser?: any | null;
}

const PricingCard = ({
  plan,
  features = [],
  isOneTime = false,
  inModal = false,
  onPlanSelected,
  billingCycle = "monthly",
  allPlans = [],
  
  hasUsedFreeTrial = false,
  hasPlan = null,
  isLoadingPlans = false, // ← NEW
  currentUser = null,
}: PricingCardProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [confirmFreeDialogOpen, setConfirmFreeDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [isTrialCheckoutLoading, setIsTrialCheckoutLoading] = useState(false);

  const trialEligible = !hasPlan && isTrialEligiblePlan(plan, hasUsedFreeTrial);

  // ────────────────────────────────────────────────
  //  Loading state inside the card
  // ────────────────────────────────────────────────
  if (isLoadingPlans) {
    return (
      <Card className="relative h-full flex items-center justify-center bg-muted/50">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground font-medium">
            Loading plan details...
          </p>
        </CardContent>
      </Card>
    );
  }

  // ────────────────────────────────────────────────
  //  Normal card content (only shown when !isLoadingPlans)
  // ────────────────────────────────────────────────

  const getMonthlyPlan = () => {
    if (isOneTime) return null;
    const baseName = displayBaseName(plan.name);
    return allPlans.find(
      (p) =>
        displayBaseName(p.name) === baseName &&
        p.billing_cycle === "monthly"
    );
  };

  const monthlyPlan = getMonthlyPlan();

  const monthlyPrice =
    billingCycle === "annual"
      ? Number(monthlyPlan?.price || 0)
      : Number(plan.price || 0);

  const annualFullPrice = monthlyPrice * 12;
  const annualActualPrice =
    billingCycle === "annual" ? Number(plan.price || 0) : 0;
  const annualSavings = annualFullPrice - annualActualPrice;

  const isFreePlan = isOneTime
    ? Number(plan.price) === 0
    : billingCycle === "monthly"
    ? monthlyPrice === 0
    : annualActualPrice === 0;

  const displayFeatures = Array.isArray(features) && features.length > 0
    ? features
    : Array.isArray(plan?.features) 
      ? plan.features 
      : [];

  const handleCheckout = async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      toast({
        title: "Login required",
        variant: "destructive",
      });
      navigate("/signup");
      return;
    }

    setIsLoading(true);

    try {
      const res = await axiosInstance.post(endpoints.subscribe, {
        plan_id: plan.id,
        billing_cycle: billingCycle,
      });

      console.debug("[Pricing] handleCheckout response:", res?.data);

      // If backend returned a checkout_url, redirect to Stripe Checkout
      if (res.data.checkout_url) {
        console.debug("[Pricing] checkout_url present, invoking callback with checkoutUrl", res.data.checkout_url);
        if (inModal && onPlanSelected) {
          // Log the handoff
          console.debug("[Pricing] calling onPlanSelected with plan.id=", plan?.id, "checkoutUrl=", res.data.checkout_url);
          // Pass the checkout URL directly to the parent instead of relying on sessionStorage
          onPlanSelected(plan, res.data.checkout_url);
          return;
        }
        // Non-modal flow: redirect immediately
        console.debug("[Pricing] non-modal redirect to checkout_url");
        window.location.href = res.data.checkout_url;
        return;
      } else if (res.data.success) {
        console.debug("[Pricing] backend returned success without checkout_url (possibly immediate activation)", res.data);
        toast({ title: "Plan activated", description: res.data.message || "Your plan was activated.", variant: "default" });
        try {
          const me = await axiosInstance.get(endpoints.me);
          localStorage.setItem("user", JSON.stringify(me.data));
          localStorage.setItem("has_plan", me.data.plan ? "true" : "false");
        } catch (e) {
          // ignore
        }
        if (inModal && onPlanSelected) {
          onPlanSelected(plan, null);
          return;
        }
        navigate("/dashboard", { replace: true });
      } else {
        throw new Error("Checkout URL was not returned");
      }
    } catch (err: any) {
      toast({
        title: "Payment initiation failed",
        description:
          err.response?.data?.error ||
          err.message ||
          "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startCheckout = () => {
    // One-time plans marked free (plan.is_free) activate for $0 for any user —
    // show a confirmation dialog first. This mirrors the backend's Plan.is_free
    // check so the dialog only appears when activation will actually be free.
    const eligibleForOneTimeFree = isOneTime && Boolean(plan?.is_free ?? true);

    if (eligibleForOneTimeFree) {
      setConfirmFreeDialogOpen(true);
      return;
    }

    if (trialEligible) {
      setTrialDialogOpen(true);
      return;
    }
    handleCheckout();
  };

  const confirmTrialCheckout = async () => {
    setIsTrialCheckoutLoading(true);
    setTrialDialogOpen(false);
    await handleCheckout();
    setIsTrialCheckoutLoading(false);
  };

  return (
    <>
      <Card className="relative hover:shadow-xl transition">
        {/* {isOneTime && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-800 text-white px-4 py-1.5 rounded-full text-sm font-bold z-10 shadow-lg">
          One-Time
        </div>
      )} */}

      {/* {showAnnualSavings && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-800 text-white px-4 py-1.5 rounded-full text-sm font-bold z-10 shadow-lg">
            Save 10%
          </div>
        )} */}


        <CardContent className="p-8 flex flex-col h-full">
          <h3 className="text-xl font-bold text-center mb-4">
            <div className="flex items-center justify-center gap-3">
              <span>{isOneTime ? "One-Time" : displayBaseName(plan.name)}</span>
            </div>
          </h3>

          <div className="text-center mb-6">
            {isOneTime ? (
              Number(plan.price) === 0 ? (
                <span className="text-4xl font-bold">Free</span>
              ) : (
                <span className="text-4xl font-bold">${plan.price}</span>
              )
            ) : billingCycle === "monthly" ? (
              monthlyPrice === 0 ? (
                <span className="text-4xl font-bold">Free</span>
              ) : (
                <>
                  <span className="text-4xl font-bold">
                    ${monthlyPrice.toFixed(2)}
                  </span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </>
              )
            ) : billingCycle === "annual" && monthlyPrice > 0 ? (
              annualActualPrice === 0 ? (
                <span className="text-4xl font-bold">Free</span>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Annual Billing</p>
                  <p className="text-lg line-through text-muted-foreground font-medium">
                    ${annualFullPrice.toFixed(2)}
                  </p>
                  <p className="text-3xl font-bold text-black-600">
                    ${annualActualPrice.toFixed(2)}
                  </p>
                  <p className="text-xs text-green-600 font-medium">
                    Save ${annualSavings.toFixed(2)}/year
                  </p>
                </div>
              )
            ) : null}
          </div>

          <ul className="space-y-2 flex-1 mb-6">
            {displayFeatures.map((f: any) => (
              <li key={f.id} className="flex items-center gap-2">
                {f.status ? (
                  <Check className="h-4 w-4 shrink-0 text-green-600" />
                ) : (
                  <X className="h-4 w-4 shrink-0 text-red-500" />
                )}
                <span
                  className={f.status ? "" : "line-through text-muted-foreground"}
                >
                  {f.name}
                </span>
              </li>
            ))}
          </ul>

         <Button
          variant="cta"
          size="lg"
          onClick={startCheckout}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Processing...
            </>
          ) : (
            <>
              {hasPlan && !isFreePlan
                ? "Upgrade Now"
                : trialEligible
                ? "Start 7 Days Free Trial"
                : isFreePlan
                ? "Get Started"
                : `Go ${isOneTime ? "One-Time" : displayBaseName(plan.name)}`}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmFreeDialogOpen} onOpenChange={setConfirmFreeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Activate Free Plan</DialogTitle>
            <DialogDescription>
              This plan gives you 1 free launch every month. Do you want to activate it now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => setConfirmFreeDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="cta"
              onClick={async () => {
                setConfirmFreeDialogOpen(false);
                await handleCheckout();
              }}
              disabled={isLoading}
            >
              {isLoading ? "Activating..." : "Activate Free Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>7-Day Free Trial</DialogTitle>
            <DialogDescription>
              This subscription includes a 7-day free trial with up to 2 free launches.
              After 7 days, your remaining plan credits will be added to your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => setTrialDialogOpen(false)}
              disabled={isTrialCheckoutLoading}
            >
              Cancel
            </Button>
            <Button
              variant="cta"
              onClick={confirmTrialCheckout}
              disabled={isTrialCheckoutLoading}
            >
              {isTrialCheckoutLoading ? "Continuing..." : "Continue to Checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

};

export default Pricing;
